import type { DashboardAccent, DashboardLayoutId, DashboardTileViz } from './DashboardContract';

/**
 * The predefined dashboard layouts — a named, slotted template registry shared by
 * the BFF composer (which picks a layout id and assigns tiles to its slots) and
 * the frontend grid (which renders the slots). Pure data, so it lives in the
 * shared assist module and is importable by both the tsc-built BFF and the
 * Vite-built frontend.
 *
 * Slot `role`/`suggests` are model-facing guidance (what belongs in a slot), not
 * enforced — any tile viz may fill any slot.
 */

export type SlotSpan = number | 'full';

export interface DashboardSlot {
    /** Stable slot id within the layout; wire frames address tiles by this. */
    id: string;
    /** Human/model-facing role of the slot. */
    role: string;
    /** Column span: a number of columns, or 'full' for the whole row. */
    span: SlotSpan;
    /**
     * Layout-controlled accent for this slot's tile (the renderer tints the tile
     * and colors its chart this hue, overriding the tile's own accent). Used to
     * paint a two-tone split (comparison sides) or mark a hero/region.
     */
    accent?: DashboardAccent;
    /** When 'hero', the tile renders larger/dominant (taller body, bigger title). */
    emphasis?: 'hero';
    /** Suggested viz kinds for this slot. */
    suggests?: DashboardTileViz[];
}

export interface DashboardLayoutDef {
    id: DashboardLayoutId;
    name: string;
    /** Model-facing: when to choose this layout. */
    description: string;
    columns: { base: number; sm?: number; md?: number; lg?: number; xl?: number };
    slots: DashboardSlot[];
}

export const DASHBOARD_LAYOUTS: Record<DashboardLayoutId, DashboardLayoutDef> = {
    overview: {
        id: 'overview',
        name: 'Overview',
        description: 'A broad scan of a whole area: a headline KPI strip, a dominant primary chart, then balanced supporting tiles. The default for open-ended asks.',
        columns: { base: 1, sm: 2, lg: 3 },
        slots: [
            { id: 'headline', role: 'Headline metrics across the whole view', span: 'full', suggests: ['stat-band', 'kpi'] },
            { id: 'primary', role: 'The primary breakdown or trend', span: 2, suggests: ['ranked-bars', 'trend', 'matrix'] },
            { id: 'secondary', role: 'A complementary share/mix view', span: 1, suggests: ['donut', 'kpi'] },
            { id: 'supporting-a', role: 'A supporting breakdown', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'supporting-b', role: 'A second supporting breakdown', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'supporting-c', role: 'A trend or detail to round out the picture', span: 1, suggests: ['trend', 'kpi'] },
        ],
    },
    focus: {
        id: 'focus',
        name: 'Focus',
        description: 'One subject in depth: a headline strip, a large lead visualization, and a few tight supporting tiles. Pick when the ask is about a single topic.',
        columns: { base: 1, sm: 2, lg: 3 },
        slots: [
            { id: 'headline', role: 'Headline metrics for the subject', span: 'full', suggests: ['stat-band', 'kpi'] },
            { id: 'lead', role: 'The large primary visualization of the subject', span: 2, suggests: ['trend', 'matrix', 'ranked-bars'] },
            { id: 'aside', role: 'The key share or single KPI beside the lead', span: 1, suggests: ['donut', 'kpi'] },
            { id: 'support-a', role: 'A supporting cut', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'support-b', role: 'A second supporting cut', span: 1, suggests: ['ranked-bars', 'kpi'] },
        ],
    },
    grid: {
        id: 'grid',
        name: 'Grid',
        description: 'An even gallery of equally-weighted tiles. Pick when several comparable breakdowns matter and none dominates.',
        columns: { base: 1, sm: 2, lg: 3 },
        slots: [
            { id: 'g1', role: 'A breakdown', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'g2', role: 'A breakdown', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'g3', role: 'A breakdown', span: 1, suggests: ['donut', 'kpi'] },
            { id: 'g4', role: 'A breakdown', span: 1, suggests: ['ranked-bars', 'trend'] },
            { id: 'g5', role: 'A breakdown', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'g6', role: 'A breakdown', span: 1, suggests: ['trend', 'kpi'] },
        ],
    },
    report: {
        id: 'report',
        name: 'Report',
        description: 'A top-to-bottom narrative: headline numbers, a primary trend, a side-by-side comparison, then a detailed breakdown. Pick for "report" / "summary" asks.',
        columns: { base: 1, md: 2 },
        slots: [
            { id: 'headline', role: 'Headline metrics', span: 'full', suggests: ['stat-band', 'kpi'] },
            { id: 'primary', role: 'The primary trend or breakdown over the full width', span: 'full', suggests: ['trend', 'ranked-bars', 'matrix'] },
            { id: 'compare-a', role: 'Left side of a comparison', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'compare-b', role: 'Right side of a comparison', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'detail', role: 'A detailed breakdown to close the report', span: 'full', suggests: ['matrix', 'ranked-bars'] },
        ],
    },
    comparison: {
        id: 'comparison',
        name: 'Comparison',
        description:
            'Two peer subjects measured the same way, side by side (this period vs last, team A vs team B, work vs personal, going-well vs needs-attention). The left column is one color and the right another; each left tile is mirrored by a right tile using the SAME visualization, field, and metric, differing only by a side filter. Pick when the ask is fundamentally X versus Y and both sides deserve equal weight — not one subject in depth (use focus) or a single dominant number (use headline).',
        columns: { base: 1, sm: 2, md: 2, lg: 2 },
        slots: [
            { id: 'verdict', role: 'Full-width verdict: a stat-band of the headline metric(s) for both sides so the gap reads before the columns.', span: 'full', suggests: ['stat-band', 'kpi'] },
            { id: 'left-headline', role: 'LEFT side headline number. Mirror of right-headline — the same metric for side A.', span: 1, accent: 'teal', suggests: ['kpi'] },
            { id: 'right-headline', role: 'RIGHT side headline number — the SAME metric for side B. Mirror of left-headline.', span: 1, accent: 'amber', suggests: ['kpi'] },
            { id: 'left-primary', role: 'LEFT side primary breakdown (side A). Same viz/field/metric as right-primary, side-A filter.', span: 1, accent: 'teal', suggests: ['ranked-bars', 'trend'] },
            { id: 'right-primary', role: 'RIGHT side primary breakdown — the SAME viz/field/metric as left-primary, side-B filter. The core mirrored pair.', span: 1, accent: 'amber', suggests: ['ranked-bars', 'trend'] },
            { id: 'left-mix', role: 'LEFT side share/mix (side A). Same field as right-mix.', span: 1, accent: 'teal', suggests: ['donut', 'ranked-bars'] },
            { id: 'right-mix', role: 'RIGHT side share/mix — the SAME field for side B. Mirror of left-mix.', span: 1, accent: 'amber', suggests: ['donut', 'ranked-bars'] },
        ],
    },
    headline: {
        id: 'headline',
        name: 'Headline',
        description:
            'One striking thing leads and everything else supports it. The hero names the focus and shows the single most interesting fact (a big number, a runaway ranking, or a sharp trend), with the finding stated in its title; the tiles below contribute, explain, or relate to it, all referencing the same subject. Pick for the big story or a single punchy answer — not a balanced scan (use overview) or X versus Y (use comparison).',
        columns: { base: 1, sm: 2, lg: 3 },
        slots: [
            { id: 'hero', role: 'THE headline: one dominant tile that names the focus and shows the most interesting fact, with the finding in its title. Usually a kpi, or a striking trend/ranked-bars.', span: 'full', accent: 'indigo', emphasis: 'hero', suggests: ['kpi', 'trend', 'ranked-bars'] },
            { id: 'supporting', role: 'Supporting context for the hero — its trajectory over time, or a framing second metric, so the hero is not a number without a story.', span: 1, suggests: ['trend', 'kpi'] },
            { id: 'contributing', role: 'What drives the hero — the breakdown that decomposes it (same metric as the hero, grouped by the most relevant field).', span: 1, suggests: ['ranked-bars', 'donut'] },
            { id: 'related', role: 'A related angle that rounds out the story — a share/mix or a second contributing cut on the same subject.', span: 1, suggests: ['donut', 'ranked-bars'] },
        ],
    },
    spotlight: {
        id: 'spotlight',
        name: 'Spotlight',
        description:
            'One metric in depth: the number, its trajectory, and what drives it. The top is a paired hero duo — the big number beside its trend — and the SAME metric appears in every tile, with only the group-by field changing. Pick for a single-metric deep dive (narrower than headline).',
        columns: { base: 1, sm: 2, lg: 3 },
        slots: [
            { id: 'the-number', role: 'THE metric as one big headline number (kpi). The subject of the spotlight.', span: 1, accent: 'amber', emphasis: 'hero', suggests: ['kpi'] },
            { id: 'trajectory', role: 'The SAME metric over time (trend), beside the number, so up-or-down reads instantly.', span: 2, accent: 'amber', emphasis: 'hero', suggests: ['trend'] },
            { id: 'driver-primary', role: 'The #1 driver — the SAME metric ranked by the field that most explains it.', span: 1, suggests: ['ranked-bars'] },
            { id: 'driver-secondary', role: 'A second driver — the SAME metric by a DIFFERENT field.', span: 1, suggests: ['ranked-bars'] },
            { id: 'driver-mix', role: 'The composition of the metric — a donut share across a small categorical field.', span: 1, suggests: ['donut'] },
        ],
    },
    'cross-tab': {
        id: 'cross-tab',
        name: 'Cross-tab',
        description:
            'A genuinely two-dimensional question (X by Y where both dimensions matter), read like a contingency table: a dominant matrix plus its marginals — the row totals and the column totals on the SAME metric. Pick when the interaction of two fields is the point; use one metric across the matrix and both marginals so the margins sum to the table.',
        columns: { base: 1, md: 2, lg: 3 },
        slots: [
            { id: 'grand-total', role: 'Full-width grand totals — a stat-band of the headline metric(s) across the whole table, the denominator for the margins.', span: 'full', suggests: ['stat-band'] },
            { id: 'matrix', role: 'THE cross-tab — a matrix of primaryField (rows) by secondaryField (columns) on the headline metric. The analytical centerpiece.', span: 2, suggests: ['matrix'] },
            { id: 'row-marginal', role: 'The ROW totals — ranked-bars of the SAME metric grouped by the matrix ROW field alone (the matrix collapsed across columns).', span: 1, accent: 'teal', suggests: ['ranked-bars'] },
            { id: 'col-marginal', role: 'The COLUMN totals — the SAME metric grouped by the matrix COLUMN field alone (collapsed across rows).', span: 'full', accent: 'violet', suggests: ['ranked-bars', 'donut'] },
        ],
    },
    quadrant: {
        id: 'quadrant',
        name: 'Quadrant',
        description:
            'A 2x2 of four complementary, equally weighted lenses on ONE subject — WHO (people/org), WHAT (task/topic mix), WHEN (trend over time), and HOW-WELL (quality: confidence or output) — with the subject filter applied to all four. Pick for a balanced 360 review of a single subject where no lens dominates.',
        columns: { base: 1, sm: 2 },
        slots: [
            { id: 'who', role: 'WHO — the people/org breakdown of the subject (top users / departments / org units).', span: 1, accent: 'indigo', suggests: ['ranked-bars', 'donut'] },
            { id: 'what', role: 'WHAT — the activity mix (task category / subcategory / topic, as a share or ranking).', span: 1, accent: 'teal', suggests: ['donut', 'ranked-bars'] },
            { id: 'when', role: 'WHEN — the trend of the subject over time (headline metric by week/month).', span: 1, accent: 'amber', suggests: ['trend'] },
            { id: 'how', role: 'HOW-WELL — the quality/intensity lens (avg confidence or avg output) grouped by a relevant field.', span: 1, accent: 'violet', suggests: ['ranked-bars', 'matrix'] },
        ],
    },
};

export function findLayout(id: DashboardLayoutId | string | undefined): DashboardLayoutDef | undefined {
    if (id === undefined) return undefined;
    return DASHBOARD_LAYOUTS[id as DashboardLayoutId];
}

/** The slot ids of a layout, in render order. */
export function layoutSlotIds(id: DashboardLayoutId): string[] {
    return DASHBOARD_LAYOUTS[id].slots.map((slot) => slot.id);
}
